import React, { useEffect, useState } from "react";
import { AxiosError } from "axios";

import { Constants } from "@eagerdog/constants";
import {
  INotificationPreferences,
  IUser,
  NotificationChannel,
  SocialNotificationCategory,
  resolveNotificationPreferences,
} from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { localStorageService } from "src/services/local-storage.service";
import { toast } from "src/components/Toast/ToastManager";
import Toggle from "src/components/Toggle/Toggle";

import styles from "./NotificationsTab.module.scss";

interface IProps {
  user: IUser;
  onSaved(user: IUser): void;
  /** Category an unsubscribe link just switched off, for the confirmation toast. */
  unsubscribed?: string | null;
}

/** What each category controls, so people know what they are switching off. */
const CATEGORY_HINT: Record<SocialNotificationCategory, string> = {
  [Constants.social_notification_category.activity]:
    "Likes and shares on your posts, new followers, titles your dog earns, and results a club enters for your dog.",
  [Constants.social_notification_category.following]:
    "New posts, runs and titles from the dogs and clubs you follow. Emails are grouped so you get at most one a day.",
  [Constants.social_notification_category.reminders]:
    "A nudge to share your run after an event, and a catch-up when you have been away for a while.",
};

const CATEGORIES = Object.values(Constants.social_notification_category) as SocialNotificationCategory[];
const CHANNELS = Object.values(Constants.notification_channel) as NotificationChannel[];
const CHANNEL_LABEL: Record<NotificationChannel, string> = { email: "Email", push: "Push" };

/**
 * Account → Notifications (data/prd/SOCIAL_NOTIFICATIONS_PRD.md Decision 3).
 * Same save path as the points reminder setting: PATCH self, store the
 * returned user, toast.
 */
const NotificationsTab: React.FC<IProps> = ({ user, onSaved, unsubscribed }) => {
  const [prefs, setPrefs] = useState<INotificationPreferences>(resolveNotificationPreferences(user.notification_preferences));
  const [saving, setSaving] = useState<boolean>(false);

  useEffect(() => {
    setPrefs(resolveNotificationPreferences(user.notification_preferences));
  }, [user.notification_preferences]);

  useEffect(() => {
    if (!unsubscribed) return;
    const label = Constants.social_notification_category_label[unsubscribed as SocialNotificationCategory];
    if (!label) return;
    toast.show({
      title: "Notifications",
      content: `You will no longer get "${label}" emails. You can turn them back on here.`,
      duration: 8000,
      type: "success",
    });
  }, [unsubscribed]);

  const save = (category: SocialNotificationCategory, channel: NotificationChannel, enabled: boolean) => {
    const next: INotificationPreferences = {
      ...prefs,
      [category]: { ...prefs[category], [channel]: enabled },
    };
    setPrefs(next);
    setSaving(true);
    apiService.updateSelf({ notification_preferences: next }).then((response) => {
      localStorageService.setItem(localStorageService.USER, JSON.stringify(response));
      onSaved(response);
    }).catch((error: AxiosError) => {
      setPrefs(prefs);
      toast.show({
        title: "Notifications",
        content: "Couldn't save your notification settings.",
        duration: 8000,
        errorDetails: error,
        type: "fail",
      });
    }).finally(() => {
      setSaving(false);
    });
  };

  return (
    <div className="settingsTab">
      <div className="description">
        <div className="title">Notifications</div>
        <div className="text">
          Choose what Eager Dog sends you. Account and registration emails (receipts, entries, password resets) are always sent.
        </div>
      </div>
      <div className={`inputs ${styles.NotificationsTab}`}>
        <div className={styles.list}>
          {CATEGORIES.map((category) => (
            <div key={category} className={styles.row}>
              <div className={styles.copy}>
                <p className={styles.rowTitle}>{Constants.social_notification_category_label[category]}</p>
                <p className={styles.hint}>{CATEGORY_HINT[category]}</p>
              </div>
              <div className={styles.toggles}>
                {CHANNELS.map((channel) => (
                  <div key={channel} className={styles.toggleCell}>
                    <span className={styles.channelLabel}>{CHANNEL_LABEL[channel]}</span>
                    <Toggle
                      id={`notif-${category}-${channel}`}
                      checked={prefs[category][channel]}
                      disabled={saving}
                      onChange={(checked) => save(category, channel, checked)}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className={styles.footnote}>
          Push notifications arrive on the Eager Dog mobile app when you are signed in there.
        </p>
      </div>
    </div>
  );
};

export default NotificationsTab;
